<script setup lang="ts">
import { useToastStore } from '@/stores/toast'

/**
 * 화면 <b>가운데</b>에 뜨는 저장 알림.
 *
 * <p>긴 화면에서는 저장 단추가 맨 아래에 있고 알림 줄은 맨 위에 떠서,
 * <b>눌러도 아무 일이 없는 것처럼</b> 보였습니다(2026-09-02 담당자 지적).
 * 그래서 눈이 있는 자리 — 화면 한가운데 — 에 잠깐 띄웁니다.</p>
 *
 * <p><b>클릭을 막지 않습니다.</b> 알림이 떠 있는 동안에도 아래 화면을 그대로
 * 쓸 수 있어야 합니다. 알림 자체만 눌러서 지울 수 있습니다.</p>
 */
const toast = useToastStore()
</script>

<template>
  <Transition name="toast">
    <div v-if="toast.current" class="layer" aria-live="polite" role="status">
      <button
        type="button" class="box" :class="toast.current.kind"
        title="눌러서 닫습니다" @click="toast.dismiss()"
      >
        <span class="mark" aria-hidden="true">{{ toast.current.kind === 'ok' ? '✓' : '!' }}</span>
        <span class="text">{{ toast.current.message }}</span>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
/* 화면을 덮되 <b>클릭은 통과시킵니다</b> — 알림이 일을 막으면 안 됩니다. */
.layer {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1000;
}

.box {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: min(520px, 82vw);
  padding: 16px 22px;
  border: 0;
  border-radius: 12px;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 10px 34px rgba(15, 27, 45, .28);
}

.box.ok { background: var(--ok); color: #ffffff; }
.box.err { background: var(--bad); color: #ffffff; }

.mark {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, .16);
  font-size: 14px;
  font-weight: 700;
}

.text { text-align: left; }

.toast-enter-active { transition: opacity .12s ease, transform .12s ease; }
.toast-leave-active { transition: opacity .22s ease, transform .22s ease; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: scale(.96); }

/* 움직임을 줄여 달라고 한 사람에게는 나타났다 사라지기만 합니다. */
@media (prefers-reduced-motion: reduce) {
  .toast-enter-active, .toast-leave-active { transition: opacity .1s linear; }
  .toast-enter-from, .toast-leave-to { transform: none; }
}
</style>
